import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface FanficCardSkeletonProps {
  variant?: 'default' | 'similar' | 'compact';
}

export function FanficCardSkeleton({ variant = 'default' }: FanficCardSkeletonProps) {
  const variantClasses = {
    default: {
      container: 'w-full aspect-[3/4]',
      image: 'aspect-[3/4]',
      title: 'text-base',
      content: 'justify-between',
      padding: 'p-3 py-2',
      rating: 'h-6 w-12',
      focus: 'h-6 w-16',
      buttons: 'h-8 w-8',
      titleHeight: 'h-5',
      badgeHeight: 'h-6',
      textHeight: 'h-4',
      likeHeight: 'h-7',
      topPosition: 'top-3.5',
      leftPosition: 'left-3.5',
      rightPosition: 'right-3.5',
    },
    similar: {
      container: 'w-full aspect-[3/4]',
      image: 'aspect-[3/4]',
      title: 'text-base',
      content: 'justify-between',
      padding: 'p-3 py-2',
      rating: 'h-6 w-12',
      focus: 'h-6 w-16',
      buttons: 'h-8 w-8',
      titleHeight: 'h-5',
      badgeHeight: 'h-6',
      textHeight: 'h-4',
      likeHeight: 'h-7',
      topPosition: 'top-3.5',
      leftPosition: 'left-3.5',
      rightPosition: 'right-3.5',
    },
    compact: {
      container: 'h-[420px] w-[160px]',
      image: 'h-[200px] w-full',
      title: 'text-xs',
      content: 'justify-between overflow-hidden',
      padding: 'p-2 py-1.5',
      rating: 'h-4 w-8',
      focus: 'h-4 w-10',
      buttons: 'h-6 w-6',
      titleHeight: 'h-3',
      badgeHeight: 'h-4',
      textHeight: 'h-3',
      likeHeight: 'h-6',
      topPosition: 'top-3.5',
      leftPosition: 'left-3.5',
      rightPosition: 'right-3.5',
    },
  };

  const classes = variantClasses[variant] || variantClasses.default;

  return (
    <div
      className={cn(
        'rounded-2xl overflow-hidden flex flex-col bg-white shrink-0',
        classes.container,
      )}
    >
      {/* Область изображения обложки */}
      <div
        className={cn(
          'relative overflow-hidden flex-shrink-0 rounded-2xl',
          classes.image,
          'bg-[#F3F4F6] p-1.5',
        )}
      >
        {/* Скелетон обложки фанфика */}
        <Skeleton
          className={cn('w-full h-full rounded-2xl')}
          style={{ backgroundColor: '#D1D5DB' }}
        />

        {/* Левая верхняя часть: рейтинг и фокус (Гет/Джен) */}
        <div
          className={cn(
            'absolute flex items-center gap-1',
            classes.topPosition,
            classes.leftPosition,
          )}
        >
          {/* Скелетон рейтинга (18+, 16+ и т.д.) */}
          <Skeleton
            className={cn('rounded-full', classes.rating)}
            style={{ backgroundColor: '#E5E7EB' }}
          />
          {/* Скелетон бейджа фокуса (Гет/Джен) */}
          <Skeleton
            className={cn('rounded-full', classes.focus)}
            style={{ backgroundColor: '#E5E7EB' }}
          />
        </div>

        {/* Правая верхняя часть: кнопки действий */}
        <div
          className={cn(
            'absolute flex items-center gap-1',
            classes.topPosition,
            classes.rightPosition,
          )}
        >
          {/* Скелетон кнопки добавления в библиотеку */}
          <Skeleton
            className={cn('rounded-md', classes.buttons)}
            style={{ backgroundColor: '#E5E7EB' }}
          />
          {/* Скелетон кнопки информации о фанфике */}
          <Skeleton
            className={cn('rounded-md', classes.buttons)}
            style={{ backgroundColor: '#E5E7EB' }}
          />
        </div>
      </div>

      {/* Область контента карточки */}
      <div
        className={cn(
          'flex-1 flex flex-col min-h-0',
          classes.padding,
          classes.content,
          variant === 'compact' && 'flex-1',
        )}
      >
        {/* Верхняя часть контента: заголовок и метаданные */}
        <div className={cn(variant === 'compact' && 'flex-1')}>
          {/* Скелетон заголовка фанфика (первая строка) */}
          <Skeleton
            className={cn(
              'rounded mb-2',
              classes.titleHeight,
              variant === 'compact' ? 'w-3/4' : 'w-3/4',
            )}
            style={{ backgroundColor: '#E5E7EB' }}
          />
          {/* Скелетон заголовка фанфика (вторая строка, только для default) */}
          {variant !== 'compact' && (
            <Skeleton
              className={cn('rounded mb-2', classes.titleHeight, 'w-1/2')}
              style={{ backgroundColor: '#E5E7EB' }}
            />
          )}

          {/* Скелетон бейджа типа (Ориджинал/Фандом) */}
          <Skeleton
            className={cn(
              'rounded-[10px] mb-2',
              classes.badgeHeight,
              variant === 'compact' ? 'w-20' : 'w-24',
            )}
            style={{ backgroundColor: '#E5E7EB' }}
          />

          {/* Скелетон описания персонажей (только для default) */}
          {variant !== 'compact' && (
            <>
              <Skeleton
                className={cn('rounded mt-2', classes.textHeight, 'w-full')}
                style={{ backgroundColor: '#E5E7EB' }}
              />
              <Skeleton
                className={cn('rounded mt-1', classes.textHeight, 'w-4/5')}
                style={{ backgroundColor: '#E5E7EB' }}
              />
            </>
          )}
        </div>

        {/* Нижняя часть контента: статус, статистика, действия */}
        <div
          className={cn(
            'space-y-2 mt-3 flex-shrink-0',
            variant === 'compact' && 'space-y-1.5 mt-auto pt-1.5',
          )}
        >
          <div
            className={cn(
              'flex items-start justify-between flex-col',
              variant === 'compact' ? 'gap-1' : 'gap-1.5',
            )}
          >
            {/* Скелетон статуса (В процессе, Завершено и т.д.) */}
            <Skeleton
              className={cn('rounded', classes.textHeight, 'w-20')}
              style={{ backgroundColor: '#E5E7EB' }}
            />
            <div className="flex items-center gap-1">
              {/* Скелетон количества просмотров */}
              <Skeleton
                className={cn('rounded', classes.textHeight, 'w-16')}
                style={{ backgroundColor: '#E5E7EB' }}
              />
              {/* Разделитель */}
              <Skeleton
                className={cn('rounded', classes.textHeight, 'w-1')}
                style={{ backgroundColor: '#E5E7EB' }}
              />
              {/* Скелетон прогресса чтения / глав */}
              <Skeleton
                className={cn('rounded', classes.textHeight, 'w-20')}
                style={{ backgroundColor: '#E5E7EB' }}
              />
            </div>
          </div>

          {/* Разделительная линия */}
          <hr className={cn('border-gray-200', variant === 'compact' && 'my-1')} />

          {/* Нижняя строка: лайки и дата */}
          <div
            className={cn(
              'flex items-center justify-between',
              variant === 'compact' && 'text-[10px]',
            )}
          >
            {/* Скелетон кнопки лайка с количеством */}
            <Skeleton
              className={cn('rounded-full', classes.likeHeight, 'w-12')}
              style={{ backgroundColor: '#E5E7EB' }}
            />
            {/* Скелетон даты обновления */}
            <Skeleton
              className={cn('rounded', classes.textHeight, 'w-16')}
              style={{ backgroundColor: '#E5E7EB' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
